import React, { Suspense } from "react";
import TinyMCEEngine from "./engines/tinymce";
import GrapesJSEngine from "./engines/grapejs";

// ----------------------------------------
// Toolbar / Plugin Presets
// ----------------------------------------
const EDITOR_PRESETS = {
    minimal: {
        plugins: "link advlist lists autoresize",
        toolbar: "bold italic underline | bullist numlist | link",
    },
    standard: {
        plugins: "link advlist lists image table code  autoresize",
        toolbar:
            "undo redo | formatselect | bold italic underline strikethrough | forecolor backcolor | bullist numlist | alignleft aligncenter alignright alignjustify | link blockquote hr | ",
    },
    extended: {
        plugins: "link advlist lists image table media code fullscreen autoresize",
        toolbar:
            "bold italic underline strikethrough | " +
            "blockquote removeformat | forecolor backcolor | " +
            "alignleft aligncenter alignright alignjustify | " +
            "numlist bullist | " +
            "link unlink | hr table image | fullscreen |"
    },
    email: {
        plugins:
            "autoresize advlist autolink lists link image charmap preview anchor searchreplace visualblocks fullscreen insertdatetime table code help wordcount autoresize",
        toolbar:
            "variables | code | bold italic | " +
            "fontselect fontsizeselect fontfamily fontsize | " +
            "underline strikethrough | " +
            "blockquote removeformat | forecolor backcolor | " +
            "alignleft aligncenter alignright alignjustify | " +
            "numlist bullist | " +
            "link unlink | hr table image | fullscreen |"
    },
};



/**
 * UniversalEditor
 * Generic abstraction over TinyMCE, GrapesJS, etc.
 *
 * Props:
 * - editor: "tinymce" | "grapesjs"
 * - preset: one of EDITOR_PRESETS keys or null
 * - mergeStrategy: "prepend" | "append" | "replace" (default: append)
 * - type: "text" | "email" | "layout"
 * - variables: boolean (enables variables dropdown in TinyMCE)
 * - config: object (toolbar/plugins/handlers)
 * - value, onChange, height, placeholder, className
 * - ...rest: forwarded to engine
 */
export default function RichTextEditor({
    editor = "tinymce",
    preset = "minimal",
    mergeStrategy = "append",
    type = "text",
    value = "",
    onChange,
    height,
    placeholder,
    config = {},
    className,
    variables = false, // 👈 new flag
    sanitize = true,
    ...rest
}) {
    // Merge helper
    const mergeField = (base = "", custom = "") => {
        if (!custom) return base;
        if (!base) return custom;
        switch (mergeStrategy) {
            case "prepend":
                return `${custom} | ${base}`;
            case "replace":
                return custom;
            case "append":
            default:
                return `${base} | ${custom}`;
        }
    };

    // Determine preset config (skip if preset=null)
    const basePreset = preset ? EDITOR_PRESETS[preset] || {} : {};

    // Final merged config
    const mergedConfig = {
        ...basePreset,
        ...config,
        plugins: mergeField(basePreset.plugins, config.plugins),
        toolbar: mergeField(basePreset.toolbar, config.toolbar),
    };

    // Shared props
    const commonProps = {
        value,
        onChange,
        height,
        placeholder,
        type,
        config: mergedConfig,
        className,
        variables,
        sanitize,
        ...rest,
    };

    // Delegate to chosen engine
    switch (editor) {
        case "grapesjs":
            return (
                <Suspense fallback={<div>Loading GrapesJS...</div>}>
                    <GrapesJSEngine {...commonProps} />
                </Suspense>
            );

        case "tinymce":
        default:
            return <TinyMCEEngine {...commonProps} />;
    }
}
